import {
  notificationReducer,
  processGroupedNotifications,
  processIndividualNotifications,
  sortDateKeys,
  type GroupingAccumulator
} from './groupingUtils'

import { type Notification, type UserNotificationResp } from 'shared/api/notifications'

export interface DisplayNotification {
  source:
    | Notification['source']['type']
    | 'connectors'
    | 'reporting_solutions'
    | 'enhanced_signals'
    | 'budget_optimiser'
    | 'attribution_models'
  title: string
  message: string
  createdAt: Date
  id?: string
  clientId?: number
  updatedAt?: Date
  updatedBy?: Date
  createdBy?: string
  severity?: Notification['severity']
  tags?: string[]
  count?: number
  notificationIds: number[]
  isRead: boolean
}

export const groupNotifications = (
  notifications: UserNotificationResp[],
  notificationPresentationConfig: Record<string, any>
): Record<string, DisplayNotification[]> => {
  /**
   * Groups and formats a list of notifications based on their creation date and
   * presentation configuration.
   *
   * Notifications are first grouped by date ('Today', 'Yesterday', 'Last 7 days', 'Older').
   * Within each date group, notifications are further processed based on presentation
   * configuration into individual items or grouped summaries.
   *
   * The final output is a record where keys are date strings (sorted with Today/Yesterday first),
   * and values are arrays of `DisplayNotification` objects (sorted by creation time within the date).
   *
   * @param notifications - An array of raw Notification objects.
   * @returns A record mapping date strings to arrays of DisplayNotification objects.
   */
  const groupedByDate = notifications.reduce<Record<string, GroupingAccumulator>>(
    (acc, notification) => notificationReducer(acc, notification, notificationPresentationConfig),
    {}
  )

  const sortedDateKeys = Object.keys(groupedByDate).sort(sortDateKeys)

  const finalGroupedNotifications: Record<string, DisplayNotification[]> = {}

  sortedDateKeys.forEach((dateKey) => {
    const group = groupedByDate[dateKey]
    if (group === undefined) {
      return
    }

    const { individuals, grouped } = group

    const individualItems = processIndividualNotifications(individuals, notificationPresentationConfig)

    const groupedItems = processGroupedNotifications(grouped, notificationPresentationConfig)

    const itemsForDate = [...individualItems, ...groupedItems]

    itemsForDate.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

    finalGroupedNotifications[dateKey] = itemsForDate
  })

  return finalGroupedNotifications
}
